import {
  generateVehicleDetailsTitle,
  ITEM_INSERTION_FLOW_SOURCES,
  type ItemData,
  type ItemResponse,
  setAt,
} from '@finn-no/mobility-item-insertion-shared';
import { getLookupConfig } from '../utils/getHuckConfig.js';

type UserLookupResponse = {
  email: string;
  displayName: string;
  postalCode?: string | null;
};

const isObject = (value: unknown): value is Record<string, any> => Boolean(value) && typeof value === 'object';

const hasId = (value: unknown): value is { id: string | number } => isObject(value) && 'id' in value;

const deriveNestedAllowedFields = (allowedFields: string[], parentKey: string) =>
  allowedFields.filter((field) => field.startsWith(`${parentKey}.`)).map((field) => field.slice(parentKey.length + 1));

const shouldIncludeField = (allowedFields: string[], key: string) =>
  allowedFields.some((field) => field === key || field.startsWith(`${key}.`));

const normalizeArrayValue = (value: unknown[]) => value.map((item) => (hasId(item) ? item.id : item));

const normalizeObjectValue = (key: string, value: Record<string, any>, allowedFields: string[]) => {
  if (key === 'model_and_make' && !hasId(value)) {
    return undefined;
  }

  if (hasId(value)) {
    return value.id;
  }

  const nestedAllowedFields = deriveNestedAllowedFields(allowedFields, key);
  return transformVehicleDetails(value, nestedAllowedFields);
};

/**
 * This function serves two different purposes:
 *
 * 1) It checks whether the details contain any fields that should not be sent as part of the request.
 *    This should optimally be fixed in vehicle-lookup-service, but we strip them away here for now
 * 2) Managed fields do not have the expected format when coming from vehicle-lookup-service. They
 *    are received as objects with id and text. We only want to send the IDs and not as an object.
 * @param details the vehicle details fetched from vehicle-lookup-service
 * @param allowedFields dot-notation list of all fields in the ad model
 * @returns
 */
const transformVehicleDetails = (details: Record<string, any>, allowedFields: string[]): Record<string, any> => {
  return Object.entries(details).reduce<Record<string, any>>((acc, [key, value]) => {
    if (!shouldIncludeField(allowedFields, key)) {
      return acc;
    }

    if (!isObject(value)) {
      acc[key] = value;
      return acc;
    }

    if (Array.isArray(value)) {
      acc[key] = normalizeArrayValue(value);
      return acc;
    }

    acc[key] = normalizeObjectValue(key, value, allowedFields);
    return acc;
  }, {});
};

/**
 * Extracts all leaf field paths from actual vehicle data.
 * Used to compare which fields have values between two variant data objects.
 *
 * Stops recursion at managed field objects (objects with 'id' property)
 * since they get normalized to just the ID value.
 *
 * @param data - Actual vehicle data object from vehicle-lookup-service
 * @returns Array of dot-notation paths for all fields with values
 *
 * @example
 * extractFieldPathsFromData({ engine: { effect: 476, fuel: { id: 4, text: 'El' } } })
 * // Returns: ['engine.effect', 'engine.fuel']
 */
const extractFieldPathsFromData = (data: Record<string, any>, prefix = ''): string[] => {
  return Object.entries(data).flatMap(([key, value]) => {
    const path = prefix ? `${prefix}.${key}` : key;

    // Stop at managed field objects (with 'id') - they're normalized to just the ID
    if (hasId(value)) {
      return path;
    }

    // Recurse into nested objects (but not arrays or primitives)
    if (isObject(value) && !Array.isArray(value)) {
      return extractFieldPathsFromData(value, path);
    }

    return path;
  });
};

/**
 * Calculates which fields need to be set to null when switching variants.
 * Compares field paths between previous and new variant data, returning
 * paths that exist in previous but not in new (fields to be cleared).
 *
 * @param previousData - Vehicle data from the previously selected variant
 * @param newData - Vehicle data from the newly selected variant
 * @param allowedFields - Field names from ad model schema (to filter valid fields)
 * @returns Array of dot-notation paths that should be set to null
 */
const calculateFieldsToNullify = (
  previousData: Record<string, any>,
  newData: Record<string, any>,
  allowedFields: string[],
): string[] => {
  const previousPaths = new Set(extractFieldPathsFromData(previousData));
  const newPaths = new Set(extractFieldPathsFromData(newData));

  return [...previousPaths]
    .filter((path) => !newPaths.has(path))
    .filter((path) => shouldIncludeField(allowedFields, path));
};

/**
 * Extracts all field names from ad model schema (field definitions).
 * Used to determine which fields are allowed in the ad model.
 *
 * @param fields - Field definitions array from /api/model/{model} response
 * @returns Array of dot-notation field names defined in the schema
 *
 * @example
 * extractFieldNamesFromSchema([{ name: 'engine', children: [{ name: 'effect' }] }])
 * // Returns: ['engine', 'engine.effect']
 */
const extractFieldNamesFromSchema = (fields: any[], parentName = ''): string[] => {
  return fields.flatMap((field) => {
    const fieldName = parentName ? `${parentName}.${field.name}` : field.name;
    if ('children' in field) {
      return [fieldName, ...extractFieldNamesFromSchema(field.children, fieldName)];
    }
    return fieldName;
  });
};

export const createItemFromVehicleLookup = async (
  adModel: string,
  vehicleDetails: Record<string, any>,
  orgId?: string,
) => {
  const config = getLookupConfig();
  const { root, csrfToken } = config;
  const adModelFieldsUrl = `${root}/api/model/${adModel}`;
  const adModelFieldsResponse = await fetch(adModelFieldsUrl);

  if (!adModelFieldsResponse.ok) {
    throw new Error(`failed to fetch ad model: ${adModel}`);
  }

  const adModelFields = await adModelFieldsResponse.json();
  const allFields = [...adModelFields['field-definitions'], ...adModelFields['referenced-field-definitions']];
  const allFieldNames = extractFieldNamesFromSchema(allFields);

  const userLookupResponse = await fetch(`${root}/api/user-lookup`);

  if (!userLookupResponse.ok) {
    throw new Error('failed to fetch user data');
  }

  const userData = (await userLookupResponse.json()) as UserLookupResponse;
  const title = generateVehicleDetailsTitle(adModel, adModelFields, vehicleDetails as ItemData);

  const createItemUrl = orgId ? `${root}/api/items/${adModel}?orgId=${orgId}` : `${root}/api/items/${adModel}`;
  const transformedDetails = transformVehicleDetails(vehicleDetails, allFieldNames);

  // Add the title to transformedDetails if it was generated
  if (title) {
    transformedDetails.title = title;
  }

  const createItemDTO = {
    data: {
      ...transformedDetails,
      contact: {
        contact_name: userData?.displayName,
        contact_email: userData?.email,
      },
      ...(userData?.postalCode && {
        address: {
          ...(transformedDetails.address || {}),
          postalcode: userData.postalCode.replace(/\s/g, ''),
        },
      }),
      item_source: {
        insertion_flow: orgId ? ITEM_INSERTION_FLOW_SOURCES.BC : ITEM_INSERTION_FLOW_SOURCES.PRIVATE,
      },
    },
  };

  const createResponse = await fetch(createItemUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-csrf-token': csrfToken,
    },
    body: JSON.stringify(createItemDTO),
  });

  if (!createResponse.ok) {
    throw new Error('Unable to create ad');
  }

  return (await createResponse.json()) as ItemResponse;
};

/**
 * Updates an existing item with vehicle lookup data.
 * Used in the new flow (TAI-1899) where item is created immediately and updated with vehicle details later.
 *
 * Unlike createItemFromVehicleLookup, this function:
 * - Does NOT fetch user data (item already has contact info from initial creation)
 * - Uses PATCH to partially update the item (preserves user-entered fields like description, price)
 * - Only sends vehicle-related fields from lookup
 * - Requires the item's etag for optimistic concurrency (passed from server-side config)
 */
export const updateItemFromVehicleLookup = async (
  itemId: string,
  adModel: string,
  vehicleDetails: Record<string, any>,
  csrfToken: string,
  root: string,
  etag: string,
  orgId?: string,
  previousVariantData?: Record<string, any>,
): Promise<ItemResponse> => {
  // Fetch ad model fields to know which fields are allowed
  const adModelFieldsUrl = `${root}/api/model/${adModel}`;
  const adModelFieldsResponse = await fetch(adModelFieldsUrl);

  if (!adModelFieldsResponse.ok) {
    throw new Error(`failed to fetch ad model: ${adModel}`);
  }

  const adModelFields = await adModelFieldsResponse.json();
  const allFields = [...adModelFields['field-definitions'], ...adModelFields['referenced-field-definitions']];
  const allFieldNames = extractFieldNamesFromSchema(allFields);

  // Transform vehicle details using the same logic as createItemFromVehicleLookup
  const transformedDetails = transformVehicleDetails(vehicleDetails, allFieldNames);

  // Generate title if applicable
  const title = generateVehicleDetailsTitle(adModel, adModelFields, vehicleDetails as ItemData);
  if (title) {
    transformedDetails.title = title;
  }

  // If previous variant data is provided, calculate fields that need to be nullified
  // These are fields that existed in the previous variant but not in the new one
  if (previousVariantData) {
    const fieldsToNullify = calculateFieldsToNullify(previousVariantData, vehicleDetails, allFieldNames);

    for (const path of fieldsToNullify) {
      setAt(transformedDetails, path, null);
    }
  }

  // PATCH the existing item with vehicle lookup data (partial update to preserve user-entered fields)
  const updateItemUrl = `${root}/api/items/${itemId}${orgId ? `?orgId=${orgId}` : ''}`;

  const updateResponse = await fetch(updateItemUrl, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'x-csrf-token': csrfToken,
      'If-Match': etag,
    },
    body: JSON.stringify({
      data: transformedDetails,
      commit: false, // Don't commit yet, user will continue editing
    }),
  });

  if (!updateResponse.ok) {
    throw new Error('Unable to update ad with vehicle data');
  }

  return (await updateResponse.json()) as ItemResponse;
};
